import ExpenseException from '@/app/models/expenseException';
import { useDeleteAllExpenseExceptionMutation } from '@/app/services/expenseExceptionApi';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { TablePagination, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ActionMenu, { ItemAction } from '../common/ActionMenu';
import CancelOrConfirmDialog from '../common/dialogs/CancelOrConfirmDialog';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import TruncatedTextWithTooltip from '../common/truncatedTextWithToolTip';

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  fontSize: 13,
  fontWeight: 'bold',
  paddingBottom: 1,
}));

const RowTableCell = styled(TableCell)({
  fontSize: 13,
});

type ExceptionTableProps = {
  rows: Array<ExpenseException>;
  page: number;
  setPage: (n: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (n: number) => void;
  count: number;
  setShowCreateModal: (b: boolean) => void;
  setShowCopyModal: (b: boolean) => void;
  setEditProject: (p: { name: string; code: string; payingName: string; payingCode: string }) => void;
  setCopyProject: (p: { destiniCode: string; code: string; exceptions: number[] }) => void;
};

function ExceptionTable(props: ExceptionTableProps) {
  const {
    rows,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    count,
    setShowCreateModal,
    setEditProject,
    setCopyProject,
    setShowCopyModal,
  } = props;
  const { t } = useTranslation(['events', 'common']);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null as unknown as { destiniCode: string; projectCode: string });

  const [deleteAllExceptions] = useDeleteAllExpenseExceptionMutation();
  const handleDeleteException = async () => {
    const response = await deleteAllExceptions(deleteId);
    setShowDeleteDialog(false);
    if ('data' in response) {
      toast.success(
        t('Casos de Exceção excluidos com sucesso!', {
          position: toast.POSITION.TOP_RIGHT,
        }),
      );
    }
  };

  return (
    <TableContainer>
      <Table size="small" aria-label="Exception Case Table">
        <TableHead>
          <TableRow>
            <HeaderTableCell align="left">{t('common:code')}</HeaderTableCell>
            <HeaderTableCell align="left">{t('common:name')}</HeaderTableCell>
            <HeaderTableCell align="left">{t('common:exception.cases')}</HeaderTableCell>
            <HeaderTableCell align="right">{t('common:actions')}</HeaderTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id}>
              <RowTableCell align="left">{row.project.code}</RowTableCell>
              <RowTableCell align="left">{row.project.name}</RowTableCell>
              <RowTableCell align="left">
                <TruncatedTextWithTooltip
                  value={row.expenses.map((item, i) => `${i !== 0 ? ',' : ''}${item}`).join('')}
                  size={100}
                />
              </RowTableCell>
              <RowTableCell align="right">
                <ActionMenu>
                  <ItemAction
                    text={t('common:copy.exception.cases')}
                    Icon={ContentCopyIcon}
                    onClick={async () => {
                      setCopyProject({
                        destiniCode: row.destini.code,
                        code: row.project.code,
                        exceptions: row.expenses,
                      });
                      setShowCopyModal(true);
                    }}
                  />
                  <ItemAction
                    text={t('edit.exception.cases')}
                    Icon={EditIcon}
                    onClick={async () => {
                      setEditProject({
                        name: row.project.name,
                        code: row.project.code,
                        payingName: row.destini.name,
                        payingCode: row.destini.code,
                      });
                      setShowCreateModal(true);
                    }}
                  />
                  <ItemAction
                    text={t('delete.all.exception.cases')}
                    Icon={DeleteIcon}
                    onClick={async () => {
                      setDeleteId({ destiniCode: row.destini.code, projectCode: row.project.code });
                      setShowDeleteDialog(true);
                    }}
                  />
                </ActionMenu>
              </RowTableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell sx={{ textAlign: 'center' }} colSpan={8}>
                <Typography variant="subtitle2">{t('there.are.no.projects.with.exception.cases.added')}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <TablePagination
        sx={{ width: '100%' }}
        rowsPerPageOptions={[5, 10, 50]}
        component="div"
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        labelRowsPerPage={t('common:items.per.page')}
      />
      <CancelOrConfirmDialog
        title={t('Delete.Exceptions')}
        open={showDeleteDialog}
        contentText={t('are.you.sure.you.want.to.delete.all.exceptions')}
        onClose={() => {
          setShowDeleteDialog(false);
        }}
        onConfirm={() => {
          handleDeleteException();
          setDeleteId(null as unknown as { destiniCode: string; projectCode: string });
        }}
        confirmText={t('common:comfirm')}
        cancelText={t('common:cancel')}
      />
    </TableContainer>
  );
}

export default ExceptionTable;
